"use client";

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  LogoAvatar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SiteNavbar,
  ToggleGroup,
  ToggleGroupItem,
} from "@pex/ui-web";
import {
  ChevronDown,
  Clock,
  ExternalLink,
  Search,
  Video,
  X,
} from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { JoinRequestModal } from "@/components/detail/join-request-modal";
import type {
  ProgrammeTypeOption,
  UrlFilters,
} from "@/components/in-person/filter-bar";
import { ContactGroupButton } from "@/components/shared/contact-group-button";
import { formatSchedule } from "@/lib/format";
import type { DiscoveryItem, FilterOptions } from "@/lib/queries/discovery";
import type { Group } from "@/lib/queries/groups";
import type { Programme } from "@/lib/queries/programmes";
import {
  matchesGroupType,
  GROUP_TYPE_OPTIONS as SHARED_GROUP_TYPE_OPTIONS,
  SPECIALISATION_LABELS,
} from "@/lib/specialisation-labels";

// ─── Constants ───────────────────────────────────────────────────────────────

const PILL =
  "rounded-full! px-3 text-sm shadow-sm bg-background! data-[state=on]:bg-[var(--brand-carrara-light)]! data-[state=on]:text-foreground! data-[state=on]:border-[var(--brand-carrara-darker)]!";

const DROPDOWN_PILL =
  "inline-flex h-9 items-center justify-center gap-1 whitespace-nowrap rounded-full border border-input bg-background px-3 font-medium text-sm shadow-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none data-[state=on]:bg-[var(--brand-carrara-light)] data-[state=on]:border-[var(--brand-carrara-darker)] data-[state=on]:text-foreground";

function ChairIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="currentColor"
      viewBox="0 -960 960 960"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M200-120v-308q0-26 17-43t43-17h60v-90h-60q-26 0-43-17t-17-43v-142q0-26 17-43t43-17h440q26 0 43 17t17 43v142q0 26-17 43t-43 17h-60v90h60q26 0 43 17t17 43v308h-60v-122H260v122h-60Zm180-368h200v-90H380v90Z" />
    </svg>
  );
}

const PROGRAMME_TAGLINES: Record<string, string> = {
  ers: "Addiction recovery foundation",
  resound: "Support for families and loved ones",
  thrive: "Personal growth and emotional wellness",
  vantage: "Freedom from pornography addiction",
};

// ─── Filter helpers (mirrored from in-person-screen) ─────────────────────────

const VALID_TYPES = ["all", "group", "programme"] as const;

function parseEnum<T extends string>(
  value: string | null,
  valid: readonly T[],
  fallback: T
): T {
  return valid.includes(value as T) ? (value as T) : fallback;
}

function fromParams(p: URLSearchParams): UrlFilters {
  return {
    group_type: p.get("grp") ?? "",
    meeting_day: p.get("day") ?? "",
    programme_type: p.get("prog") ?? "",
    type: parseEnum(p.get("type"), VALID_TYPES, "all"),
  };
}

function toParams(f: UrlFilters): string {
  const p = new URLSearchParams();
  if (f.type !== "all") {
    p.set("type", f.type);
  }
  if (f.meeting_day) {
    p.set("day", f.meeting_day);
  }
  if (f.programme_type) {
    p.set("prog", f.programme_type);
  }
  if (f.group_type) {
    p.set("grp", f.group_type);
  }
  return p.toString();
}

// Fuzzy match: first tries exact substring, then checks if all query chars
// appear in the same order anywhere in the text (handles typos like "mondya").
function fuzzyMatch(text: string, query: string): boolean {
  if (text.includes(query)) {
    return true;
  }
  let qi = 0;
  for (let ti = 0; ti < text.length && qi < query.length; ti++) {
    if (text[ti] === query[qi]) {
      qi++;
    }
  }
  return qi === query.length;
}

function matchesSearch(item: DiscoveryItem, query: string): boolean {
  const q = query.toLowerCase();
  const name =
    item.kind === "group" ? item.data.name : (item.data.definition?.name ?? "");

  const fields: (string | null | undefined)[] = [
    name,
    item.data.city,
    item.data.suburb,
    item.data.postal_code,
    item.data.organization?.name,
    item.data.meeting_day,
  ];

  if (item.kind === "group") {
    fields.push(
      item.data.country,
      (item.data.specialisation ?? "").replace(/_/g, " ")
    );
  } else {
    fields.push(item.data.definition?.code);
  }

  return fields.some((f) => fuzzyMatch((f ?? "").toLowerCase(), q));
}

function applyFilters(
  items: DiscoveryItem[],
  filters: UrlFilters,
  search: string
): DiscoveryItem[] {
  return items.filter((item) => {
    if (item.data.format !== "online") {
      return false;
    }
    if (filters.type !== "all" && item.kind !== filters.type) {
      return false;
    }
    if (search && !matchesSearch(item, search)) {
      return false;
    }
    if (
      filters.meeting_day &&
      item.data.meeting_day?.toLowerCase() !== filters.meeting_day
    ) {
      return false;
    }
    if (
      filters.programme_type &&
      item.kind === "programme" &&
      item.data.definition?.code !== filters.programme_type
    ) {
      return false;
    }
    if (
      filters.group_type &&
      item.kind === "group" &&
      !matchesGroupType(item.data.specialisation, filters.group_type)
    ) {
      return false;
    }
    return true;
  });
}

// ─── Inline cards (no sheet needed) ──────────────────────────────────────────

function OnlineGroupCard({ group }: { group: Group }) {
  const schedule = formatSchedule(
    group.meeting_day,
    group.meeting_start_time,
    group.timezone
  );
  // name is non-nullable in the schema; ?? is a defensive runtime fallback
  const displayName = group.name ?? group.organization?.name;

  return (
    <div className="space-y-3 rounded-xl border bg-card px-4 py-4 shadow-sm">
      {/* Header: avatar + name + specialisation badge */}
      <div className="flex items-start gap-3">
        <LogoAvatar
          alt={`${group.organization?.name ?? displayName} logo`}
          name={displayName}
          size={44}
          src={group.organization?.logo_url}
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="truncate font-semibold text-[15px] leading-snug">
            {displayName}
          </p>
          {schedule && (
            <p className="flex items-center gap-1 text-muted-foreground text-xs">
              <Clock className="h-3 w-3 shrink-0" />
              {schedule}
            </p>
          )}
        </div>
        {group.specialisation && (
          <Badge className="mt-0.5 shrink-0 self-start" variant="secondary">
            {SPECIALISATION_LABELS[group.specialisation] ??
              group.specialisation}
          </Badge>
        )}
      </div>

      {/* Actions */}
      {(group.online_meeting_url || group.has_contact_email) && (
        <div className="flex flex-wrap gap-2">
          {group.online_meeting_url && (
            <Button asChild size="sm" variant="outline">
              <a href={group.online_meeting_url} rel="noopener" target="_blank">
                <Video className="h-4 w-4" />
                Join Meeting
              </a>
            </Button>
          )}
          {group.has_contact_email && (
            <ContactGroupButton groupId={group.id} size="sm" />
          )}
        </div>
      )}
    </div>
  );
}

function OnlineProgrammeCard({ programme }: { programme: Programme }) {
  const [joinOpen, setJoinOpen] = useState(false);

  const { definition } = programme;
  const schedule = formatSchedule(
    programme.meeting_day,
    programme.meeting_start_time
  );
  const displayName = definition?.name ?? "Programme";
  const isOpen = programme.access === "open";

  return (
    <div className="space-y-3 rounded-xl border bg-card px-4 py-4 shadow-sm">
      {/* Header: logo + programme eyebrow + org name + access badge */}
      <div className="flex items-start gap-3">
        <LogoAvatar
          alt={`${displayName} logo`}
          name={displayName}
          size={44}
          src={definition?.logo_letter_url}
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-medium text-[11px] text-muted-foreground uppercase tracking-widest">
            {displayName}
          </p>
          <p className="truncate font-semibold text-[15px] leading-snug">
            {programme.organization?.name ?? displayName}
          </p>
          {schedule && (
            <p className="flex items-center gap-1 text-muted-foreground text-xs">
              <Clock className="h-3 w-3 shrink-0" />
              {schedule}
            </p>
          )}
        </div>
        <Badge className="mt-0.5 shrink-0 self-start" variant="secondary">
          {isOpen ? "Open" : "Invite Only"}
        </Badge>
      </div>

      {/* Actions */}
      {(isOpen || definition?.website_url) && (
        <div className="flex flex-wrap gap-2">
          {isOpen && programme.online_meeting_url && (
            <Button asChild size="sm" variant="outline">
              <a
                href={programme.online_meeting_url}
                rel="noopener"
                target="_blank"
              >
                <Video className="h-4 w-4" />
                Join Meeting
              </a>
            </Button>
          )}
          {isOpen && !programme.online_meeting_url && (
            <Button
              onClick={() => setJoinOpen(true)}
              size="sm"
              variant="outline"
            >
              Request to Join
            </Button>
          )}
          {definition?.website_url && (
            <Button asChild size="sm" variant="outline">
              <a href={definition.website_url} rel="noopener" target="_blank">
                Learn More
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      )}

      <JoinRequestModal
        onOpenChange={setJoinOpen}
        open={joinOpen}
        programmeLocationId={programme.id}
      />
    </div>
  );
}

function OnlineItemCard({ item }: { item: DiscoveryItem }) {
  if (item.kind === "group") {
    return <OnlineGroupCard group={item.data} />;
  }
  return <OnlineProgrammeCard programme={item.data} />;
}

// ─── Filter bar ───────────────────────────────────────────────────────────────

interface FilterBarProps {
  filterOptions: FilterOptions;
  filters: UrlFilters;
  groupTypeOptions: { label: string; value: string }[];
  onFiltersChange: (updates: Partial<UrlFilters>) => void;
  onSearchChange: (value: string) => void;
  programmeTypes: ProgrammeTypeOption[];
  searchText: string;
}

function OnlineFilterBar({
  filterOptions,
  filters,
  groupTypeOptions,
  onFiltersChange,
  onSearchChange,
  programmeTypes,
  searchText,
}: FilterBarProps) {
  const typeActive = filters.type !== "all";

  let typeTriggerLabel = "Type";
  if (filters.type === "group") {
    typeTriggerLabel =
      groupTypeOptions.find((o) => o.value === filters.group_type)?.label ??
      "All groups";
  } else if (filters.type === "programme") {
    typeTriggerLabel =
      programmeTypes.find((p) => p.code === filters.programme_type)?.name ??
      "All programmes";
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 sm:px-6 md:flex-row md:items-center md:gap-3 lg:px-8">
      {/* Search */}
      <div className="relative w-full md:w-96 md:shrink-0">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Search online groups"
          autoComplete="off"
          className="rounded-full bg-background pr-9 pl-9 shadow-sm"
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search groups and programmes…"
          value={searchText}
        />
        {searchText && (
          <button
            aria-label="Clear search"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => onSearchChange("")}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filter pills — horizontal scroll on mobile, no wrap */}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {/* All pill */}
        <ToggleGroup
          className="flex gap-1"
          onValueChange={(v) => {
            if (v) {
              onFiltersChange({
                group_type: "",
                programme_type: "",
                type: "all",
              });
            }
          }}
          spacing={2}
          type="single"
          value={filters.type === "all" ? "all" : ""}
          variant="outline"
        >
          <ToggleGroupItem className={PILL} value="all">
            All
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Type dropdown — only shown when at least one group type or programme exists */}
        {(groupTypeOptions.length > 0 || programmeTypes.length > 0) && (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={DROPDOWN_PILL}
              data-state={typeActive ? "on" : "off"}
            >
              {typeTriggerLabel}
              <ChevronDown className="h-3 w-3 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[min(80vw,20rem)]">
              {groupTypeOptions.length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Groups</DropdownMenuLabel>
                  {groupTypeOptions.map((o) => (
                    <DropdownMenuItem
                      key={o.value}
                      onClick={() =>
                        onFiltersChange({
                          group_type: o.value,
                          programme_type: "",
                          type: "group",
                        })
                      }
                    >
                      <ChairIcon className="size-5 shrink-0 text-muted-foreground" />
                      {o.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              )}

              {groupTypeOptions.length > 0 && programmeTypes.length > 0 && (
                <DropdownMenuSeparator />
              )}

              {programmeTypes.length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Programmes</DropdownMenuLabel>
                  {programmeTypes.map((p) => (
                    <DropdownMenuItem
                      key={p.code}
                      onClick={() =>
                        onFiltersChange({
                          group_type: "",
                          programme_type: p.code,
                          type: "programme",
                        })
                      }
                    >
                      {p.logoUrl ? (
                        // biome-ignore lint/performance/noImgElement: decorative inline logo
                        <img
                          alt=""
                          className="h-5 w-5 shrink-0 object-contain"
                          height={20}
                          src={p.logoUrl}
                          width={20}
                        />
                      ) : (
                        <span className="h-5 w-5 shrink-0" />
                      )}
                      <div className="flex flex-col gap-0.5">
                        <span>{p.name}</span>
                        {PROGRAMME_TAGLINES[p.code] && (
                          <span className="font-normal text-muted-foreground text-xs">
                            {PROGRAMME_TAGLINES[p.code]}
                          </span>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Meeting Day */}
        <Select
          onValueChange={(v) =>
            onFiltersChange({ meeting_day: v === "__any__" ? "" : v })
          }
          value={filters.meeting_day || "__any__"}
        >
          <SelectTrigger className="w-36 shrink-0 bg-background">
            <SelectValue placeholder="Any day" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any day</SelectItem>
            {filterOptions.meetingDays.map((d) => (
              <SelectItem key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  avatarUrl?: string | null;
  filterOptions: FilterOptions;
  initialFeed: DiscoveryItem[];
}

export function OnlineScreen({ avatarUrl, initialFeed, filterOptions }: Props) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const filters = useMemo(() => fromParams(searchParams), [searchParams]);
  const [searchText, setSearchText] = useState("");

  const navigate = useCallback(
    (updates: Partial<UrlFilters>) => {
      const next = { ...filters, ...updates };
      const qs = toParams(next);
      router.replace(qs ? `?${qs}` : "?", { scroll: false });
    },
    [filters, router]
  );

  const clearAll = useCallback(() => {
    setSearchText("");
    navigate({
      group_type: "",
      meeting_day: "",
      programme_type: "",
      type: "all",
    });
  }, [navigate]);

  const programmeTypes = useMemo((): ProgrammeTypeOption[] => {
    const seen = new Map<string, ProgrammeTypeOption>();
    for (const item of initialFeed) {
      if (
        item.kind === "programme" &&
        item.data.format === "online" &&
        item.data.definition
      ) {
        const { code, description, name, logo_letter_url } =
          item.data.definition;
        if (!seen.has(code)) {
          seen.set(code, { code, description, logoUrl: logo_letter_url, name });
        }
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [initialFeed]);

  const groupTypeOptions = useMemo(() => {
    const present = new Set<string>();
    for (const item of initialFeed) {
      if (
        item.kind === "group" &&
        item.data.format === "online" &&
        item.data.specialisation
      ) {
        present.add(item.data.specialisation);
      }
    }
    return SHARED_GROUP_TYPE_OPTIONS.filter((o) =>
      o.matches.some((m) => present.has(m))
    );
  }, [initialFeed]);

  const filteredItems = useMemo(
    () => applyFilters(initialFeed, filters, searchText),
    [initialFeed, filters, searchText]
  );

  const hasActiveFilters =
    !!searchText || filters.type !== "all" || !!filters.meeting_day;

  return (
    <div className="relative h-dvh overflow-hidden">
      {/* Fixed top navbar */}
      <SiteNavbar
        avatarUrl={avatarUrl}
        currentApp="finder"
        fixed
        websiteUrl={process.env.NEXT_PUBLIC_WEBSITE_URL}
      />

      {/* Single scrollable column — filter bar scrolls with content on mobile, sticky on md+ */}
      <div className="flex h-full flex-col pt-16">
        <div className="flex-1 overflow-y-auto">
          {/* Filter bar + gradient as one sticky unit — scrolls freely on mobile, sticky on md+ */}
          <div className="md:sticky md:top-0 md:z-10">
            <div className="bg-background pt-4 pb-2">
              <OnlineFilterBar
                filterOptions={filterOptions}
                filters={filters}
                groupTypeOptions={groupTypeOptions}
                onFiltersChange={navigate}
                onSearchChange={setSearchText}
                programmeTypes={programmeTypes}
                searchText={searchText}
              />
            </div>
            {/* Gradient pinned to the bottom of the sticky header — desktop only */}
            <div className="pointer-events-none hidden h-16 bg-gradient-to-b from-background to-transparent md:block" />
          </div>

          <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
            {/* Single-column list */}
            <div className="mx-auto flex max-w-2xl flex-col gap-3">
              {filteredItems.map((item) => (
                <OnlineItemCard item={item} key={item.data.id} />
              ))}
            </div>

            {/* Empty state */}
            {filteredItems.length === 0 && (
              <div className="space-y-3 py-12 text-center">
                <p className="text-muted-foreground text-sm">
                  {searchText
                    ? `No results for "${searchText}"`
                    : "No online groups match your filters."}
                </p>
                {hasActiveFilters && (
                  <button
                    className="text-primary text-sm underline underline-offset-4 transition-opacity hover:opacity-80"
                    onClick={clearAll}
                    type="button"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Bottom gradient — fades content into the floating pill */}
          <div className="pointer-events-none sticky bottom-0 z-10 -mt-20 h-20 bg-gradient-to-t from-background to-transparent" />

          {/* Bottom pill — full-width on mobile, centred pill on desktop.
              Mobile bottom pad hugs the iOS home indicator via safe-area inset. */}
          <div className="absolute right-0 bottom-0 left-0 z-20 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:right-auto sm:bottom-8 sm:left-1/2 sm:w-auto sm:-translate-x-1/2 sm:px-0 sm:pb-0">
            <Link
              className="inline-flex h-11 w-full items-center justify-center rounded-full border border-input bg-background font-medium text-sm shadow-md transition-colors hover:bg-accent sm:h-9 sm:w-auto sm:px-4"
              href="/in-person"
            >
              ← In-Person Groups
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
